/**
 * @fileoverview Metronome tool for the redesigned Tools screen ("Studio Quiet").
 * Web Audio click generation + visual beats. Audio logic is unchanged from the
 * former MetronomePage; only the presentation is restyled. Preserves the
 * metronome-page / bpm-* / beats-* / *-slider testids the suite relies on.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Icon } from "@/components/studio/primitives";

export default function MetronomeTool() {
  const [bpm, setBpm] = useState(120);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentBeat, setCurrentBeat] = useState(0);
  const [beatsPerMeasure, setBeatsPerMeasure] = useState(4);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);

  const audioContextRef = useRef(null);
  const intervalRef = useRef(null);
  const currentBeatRef = useRef(0);

  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return () => audioContextRef.current?.close();
  }, []);

  const playClick = useCallback((isAccent) => {
    if (!audioContextRef.current || isMuted) return;
    const ctx = audioContextRef.current;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = isAccent ? 1000 : 800;
    osc.type = "sine";
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.05);
  }, [volume, isMuted]);

  const startMetronome = useCallback(() => {
    if (audioContextRef.current?.state === "suspended") audioContextRef.current.resume();
    const interval = (60 / bpm) * 1000;
    const tick = () => {
      const isAccent = currentBeatRef.current === 0;
      playClick(isAccent);
      setCurrentBeat(currentBeatRef.current);
      currentBeatRef.current = (currentBeatRef.current + 1) % beatsPerMeasure;
    };
    tick();
    intervalRef.current = setInterval(tick, interval);
    setIsPlaying(true);
  }, [bpm, beatsPerMeasure, playClick]);

  const stopMetronome = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setIsPlaying(false);
    currentBeatRef.current = 0;
    setCurrentBeat(0);
  }, []);

  useEffect(() => {
    if (isPlaying) {
      stopMetronome();
      startMetronome();
    }
  }, [bpm, beatsPerMeasure, stopMetronome, startMetronome, isPlaying]);

  const adjustBpm = (delta) => setBpm(Math.max(40, Math.min(240, bpm + delta)));

  const roundBtn =
    "flex items-center justify-center rounded-full border border-[var(--studio-divider)] bg-[var(--studio-raised)] text-[var(--text-secondary)] transition-colors duration-150 hover:bg-[var(--studio-interactive)]";

  return (
    <div className="text-center" data-testid="metronome-page">
      {/* Beat indicators */}
      <div className="mb-8 flex justify-center gap-2">
        {[...Array(beatsPerMeasure)].map((_, i) => (
          <span
            key={i}
            data-testid={`beat-indicator-${i}`}
            className={`h-2.5 w-2.5 rounded-full transition-all duration-100 ${
              currentBeat === i && isPlaying
                ? "scale-125 bg-[var(--accent-yellow)] shadow-[0_0_16px_var(--accent-yellow-glow)]"
                : "bg-[var(--studio-interactive)]"
            }`}
          />
        ))}
      </div>

      {/* BPM */}
      <div className="font-mono text-[96px] font-extralight leading-none tracking-[-3px] text-[var(--text-primary)]" data-testid="bpm-display">
        {bpm}
      </div>
      <div className="mt-1.5 font-sans text-[11px] font-semibold uppercase tracking-[0.25em] text-[var(--text-tertiary)]">BPM</div>

      {/* ± controls */}
      <div className="mt-8 flex justify-center gap-[14px]">
        <button onClick={() => adjustBpm(-10)} data-testid="bpm-minus-10" className={`h-16 w-16 text-lg font-sans ${roundBtn}`}>−10</button>
        <button onClick={() => adjustBpm(-1)} data-testid="bpm-minus-1" className={`h-16 w-16 ${roundBtn}`} aria-label="Decrease BPM"><Icon name="chevronDown" size={22} /></button>
        <button onClick={() => adjustBpm(1)} data-testid="bpm-plus-1" className={`h-16 w-16 ${roundBtn}`} aria-label="Increase BPM"><Icon name="chevronUp" size={22} /></button>
        <button onClick={() => adjustBpm(10)} data-testid="bpm-plus-10" className={`h-16 w-16 text-lg font-sans ${roundBtn}`}>+10</button>
      </div>

      {/* BPM fine slider */}
      <div className="mx-auto mt-7 max-w-sm px-2">
        <Slider
          value={[bpm]}
          onValueChange={([v]) => setBpm(v)}
          min={40}
          max={240}
          step={1}
          trackClassName="bg-[var(--studio-interactive)]"
          rangeClassName="bg-[var(--accent-yellow)]"
          thumbClassName="h-5 w-5 border-0 bg-[var(--accent-yellow)] shadow-[0_0_12px_var(--accent-yellow-glow)]"
          data-testid="bpm-slider"
        />
      </div>

      {/* Play / pause */}
      <button
        onClick={isPlaying ? stopMetronome : startMetronome}
        data-testid="play-stop-button"
        className="mx-auto mt-10 flex h-[108px] w-[108px] items-center justify-center rounded-full bg-[var(--accent-yellow)] shadow-[0_8px_32px_var(--accent-yellow-glow)]"
        style={isPlaying ? { animation: "hansRecordPulse 0.5s ease-in-out infinite" } : undefined}
        aria-label={isPlaying ? "Stop" : "Start"}
      >
        <Icon name={isPlaying ? "pause" : "play"} size={40} className="text-[var(--text-inverse)]" fill="currentColor" strokeWidth={0} />
      </button>

      {/* Beats per measure */}
      <div className="mt-10 font-sans text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--text-tertiary)]">Beats per measure</div>
      <div className="mt-3.5 flex flex-wrap justify-center gap-2.5">
        {[2, 3, 4, 5, 6, 7, 8].map((n) => {
          const sel = beatsPerMeasure === n;
          return (
            <button
              key={n}
              data-testid={`beats-${n}`}
              onClick={() => { setBeatsPerMeasure(n); currentBeatRef.current = 0; }}
              className={`h-12 w-12 rounded-full font-mono text-base transition-colors duration-150 ${
                sel
                  ? "bg-[var(--accent-yellow)] font-semibold text-[var(--text-inverse)]"
                  : "border border-[var(--studio-divider)] bg-[var(--studio-raised)] text-[var(--text-secondary)]"
              }`}
            >
              {n}
            </button>
          );
        })}
      </div>

      {/* Volume */}
      <div className="mx-auto mt-8 flex w-40 items-center gap-3">
        <button
          onClick={() => setIsMuted((m) => !m)}
          data-testid="mute-button"
          aria-label={isMuted ? "Unmute" : "Mute"}
          className="text-[var(--text-tertiary)] transition-colors hover:text-[var(--text-secondary)]"
        >
          {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
        <Slider
          value={[volume * 100]}
          onValueChange={([v]) => setVolume(v / 100)}
          min={0}
          max={100}
          step={1}
          trackClassName="bg-[var(--studio-interactive)]"
          rangeClassName="bg-[var(--text-tertiary)]"
          thumbClassName="h-4 w-4 border-0 bg-[var(--text-secondary)]"
          data-testid="volume-slider"
        />
      </div>
    </div>
  );
}
